"use client";

import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import type { FollowStatusValue } from "@workspace/types";
import { FOLLOW_STATUSES } from "@workspace/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { createClient } from "@/lib/supabase/client";
import type { Database } from "@/lib/types/supabase";
import {
	USER_BLOCKED_EVENT,
	USER_UNBLOCKED_EVENT,
} from "./use-realtime-block-status";

/**
 * Modern real-time data pattern for 2025
 *
 * Key principles:
 * 1. Real-time is the PRIMARY data source, not a cache invalidator
 * 2. Local state is the single source of truth
 * 3. No manual cache invalidation - events drive everything
 * 4. Optimistic updates are temporary until real-time confirms
 * 5. Multiple button instances sync via custom events
 */

// Custom event for syncing optimistic updates across button instances
const FOLLOW_STATUS_EVENT = "follow-status-update";

interface FollowData {
	id?: string;
	followerId: string;
	followedId: string;
	status: FollowStatusValue;
	createdAt?: string;
}

interface UseRealtimeFollowStatusOptions {
	currentUserId: string | null;
	targetUserId: string | null;
	enabled?: boolean;
}

/**
 * Real-time-first follow status hook
 * This replaces tRPC queries + cache invalidation with pure real-time
 */
export function useRealtimeFollowStatus({
	currentUserId,
	targetUserId,
	enabled = true,
}: UseRealtimeFollowStatusOptions) {
	// Local state is our single source of truth
	const [followStatus, setFollowStatus] = useState<FollowStatusValue>(
		FOLLOW_STATUSES.NOT_FOLLOWING,
	);
	const [isTargetPrivate, setIsTargetPrivate] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const channelRef = useRef<any>(null); // Use any to avoid type conflicts
	const instanceId = useRef(Math.random().toString(36).substring(7)); // Unique ID for this instance
	const supabase = createClient();

	// Initial data fetch - happens ONCE
	useEffect(() => {
		if (!currentUserId || !targetUserId || !enabled) {
			setIsLoading(false);
			return;
		}

		async function fetchInitialData() {
			if (!currentUserId || !targetUserId) return;
			
			try {
				type FollowsRow = Database["public"]["Tables"]["follows"]["Row"];
				type ProfileRow = Pick<Database["public"]["Tables"]["profiles"]["Row"], "is_private">;
				
				// Get initial follow status - handle 406 errors gracefully
				const { data: followData, error: followError } = await supabase
					.from("follows")
					.select("*")
					.eq("follower_id", currentUserId)
					.eq("followed_id", targetUserId)
					.maybeSingle() as { data: FollowsRow | null; error: any };

				// Get target user privacy
				const { data: profileData, error: profileError } = await supabase
					.from("profiles")
					.select("is_private")
					.eq("id", targetUserId)
					.maybeSingle() as { data: ProfileRow | null; error: any };

				// Silently handle "no rows" errors (PGRST116)

				const status = (followData?.status || FOLLOW_STATUSES.NOT_FOLLOWING) as FollowStatusValue;
				setFollowStatus(status);
				setIsTargetPrivate(profileData?.is_private || false);
			} catch (error) {
				// No follow relationship exists
				setFollowStatus(FOLLOW_STATUSES.NOT_FOLLOWING);
			} finally {
				setIsLoading(false);
			}
		}

		fetchInitialData();
	}, [currentUserId, targetUserId, enabled, supabase]);

	// Real-time subscription - this is where the magic happens
	useEffect(() => {
		if (!currentUserId || !targetUserId || !enabled) return;

		// CRITICAL: Each hook instance needs its own unique channel
		// Otherwise Supabase will share the channel and only one callback works
		const channelName = `follow-status:${currentUserId}:${targetUserId}:${instanceId.current}`;

		// Create a unique channel for this instance
		const channel = supabase
			.channel(channelName)
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "follows",
					filter: `follower_id=eq.${currentUserId}`,
				},
				(payload: RealtimePostgresChangesPayload<FollowData>) => {
					// Client-side filter for followed_id (Supabase Realtime only supports 1 filter)
					const record = payload.eventType === "DELETE" ? payload.old : payload.new;
					if ((record as any)?.followed_id !== targetUserId) {
						return;
					}

					// Real-time update - this is our source of truth
					if (payload.eventType === "DELETE") {
						setFollowStatus(FOLLOW_STATUSES.NOT_FOLLOWING);
					} else if (payload.new) {
						setFollowStatus(payload.new.status);
					}
				},
			)
			.subscribe((status) => {
				// Handle subscription errors gracefully - errors are silently handled
				if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
					// Will retry on reconnect
				}
			});

		channelRef.current = channel;

		return () => {
			// Clean up this instance's channel
			if (channelRef.current) {
				supabase.removeChannel(channelRef.current);
			}
		};
	}, [currentUserId, targetUserId, enabled, supabase]);

	// Listen for optimistic updates from other button instances
	useEffect(() => {
		if (!currentUserId || !targetUserId) return;

		const handleOptimisticUpdate = (event: CustomEvent) => {
			const { userId, targetId, status } = event.detail;
			// Check if this event is for our follow relationship
			if (userId === currentUserId && targetId === targetUserId) {
				setFollowStatus(status);
			}
		};

		// Handle user blocked events - reset follow status when user is blocked
		const handleUserBlocked = (event: CustomEvent) => {
			const { blockerUserId, targetUserId: blockedUserId } = event.detail;
			// If current user blocked the target OR target blocked current user, reset follow status
			if (
				(blockerUserId === currentUserId && blockedUserId === targetUserId) ||
				(blockerUserId === targetUserId && blockedUserId === currentUserId)
			) {
				setFollowStatus(FOLLOW_STATUSES.NOT_FOLLOWING);
			}
		};

		// Handle user unblocked events - follow button should become available again
		const handleUserUnblocked = (event: CustomEvent) => {
			const { blockerUserId, targetUserId: unblockedUserId } = event.detail;
			// If current user unblocked the target, follow button should show "Follow" again
			// (The relationship was already removed when blocked, so it stays NOT_FOLLOWING)
			if (blockerUserId === currentUserId && unblockedUserId === targetUserId) {
				setFollowStatus(FOLLOW_STATUSES.NOT_FOLLOWING);
			}
			// Note: If target unblocked current user, we don't need to do anything
			// as the follow status is already correct
		};

		// Listen for custom events from other button instances
		window.addEventListener(
			FOLLOW_STATUS_EVENT as any,
			handleOptimisticUpdate as any,
		);

		// Listen for block/unblock events to invalidate follow status
		window.addEventListener(
			USER_BLOCKED_EVENT as any,
			handleUserBlocked as any,
		);
		window.addEventListener(
			USER_UNBLOCKED_EVENT as any,
			handleUserUnblocked as any,
		);

		return () => {
			window.removeEventListener(
				FOLLOW_STATUS_EVENT as any,
				handleOptimisticUpdate as any,
			);
			window.removeEventListener(
				USER_BLOCKED_EVENT as any,
				handleUserBlocked as any,
			);
			window.removeEventListener(
				USER_UNBLOCKED_EVENT as any,
				handleUserUnblocked as any,
			);
		};
	}, [currentUserId, targetUserId]);

	// Optimistic update function - broadcasts to ALL button instances
	const setOptimisticStatus = useCallback(
		(status: FollowStatusValue) => {
			// Update local state immediately
			setFollowStatus(status);

			// Broadcast to all other button instances on the page
			const event = new CustomEvent(FOLLOW_STATUS_EVENT, {
				detail: {
					userId: currentUserId,
					targetId: targetUserId,
					status,
				},
			});
			window.dispatchEvent(event);

			// Real-time will confirm with truth in ~100ms
		},
		[currentUserId, targetUserId],
	);

	return {
		status: followStatus,
		isTargetPrivate,
		isLoading,
		setOptimisticStatus, // For mutations to use
	};
}

/**
 * Real-time notifications hook
 * Pure event-driven, no polling, no cache
 */
export function useRealtimeNotifications(userId: string | null) {
	const [notifications, setNotifications] = useState<any[]>([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const channelRef = useRef<any>(null); // Use any to avoid type conflicts
	const supabase = createClient();

	// Initial fetch
	useEffect(() => {
		if (!userId) return;

		async function fetchInitialNotifications() {
			if (!userId) return;
			
			type NotificationRow = Database["public"]["Tables"]["notifications"]["Row"];
			
			const { data } = await supabase
				.from("notifications")
				.select("*")
				.eq("user_id", userId)
				.eq("dismissed", false)
				.order("created_at", { ascending: false })
				.limit(10) as { data: NotificationRow[] | null; error: any };

			if (data) {
				setNotifications(data);
				setUnreadCount(data.filter((n) => !n.read).length);
			}
		}

		fetchInitialNotifications();
	}, [userId, supabase]);

	// Real-time subscription
	useEffect(() => {
		if (!userId) return;

		const channel = supabase
			.channel(`notifications:${userId}`)
			.on(
				"postgres_changes",
				{
					event: "INSERT",
					schema: "public",
					table: "notifications",
					filter: `user_id=eq.${userId}`,
				},
				(payload) => {
					// Add new notification to the top
					setNotifications((prev) => [payload.new, ...prev].slice(0, 10));
					if (!payload.new.read) {
						setUnreadCount((prev) => prev + 1);
					}
				},
			)
			.on(
				"postgres_changes",
				{
					event: "UPDATE",
					schema: "public",
					table: "notifications",
					filter: `user_id=eq.${userId}`,
				},
				(payload) => {
					// If the notification was dismissed, remove it from the list
					if (payload.new.dismissed) {
						setNotifications((prev) =>
							prev.filter((n) => n.id !== payload.new.id),
						);
						// Update unread count if it was unread
						if (!payload.new.read) {
							setUnreadCount((prev) => Math.max(0, prev - 1));
						}
					} else {
						// Update existing notification (e.g., marking as read)
						setNotifications((prev) =>
							prev.map((n) => (n.id === payload.new.id ? payload.new : n)),
						);
						// Recalculate unread count
						setNotifications((prev) => {
							const unread = prev.filter((n) => !n.read && !n.dismissed).length;
							setUnreadCount(unread);
							return prev;
						});
					}
				},
			)
			.on(
				"postgres_changes",
				{
					event: "DELETE",
					schema: "public",
					table: "notifications",
					filter: `user_id=eq.${userId}`,
				},
				(payload) => {
					// Remove deleted notification
					setNotifications((prev) =>
						prev.filter((n) => n.id !== payload.old.id),
					);
					if (!payload.old.read) {
						setUnreadCount((prev) => Math.max(0, prev - 1));
					}
				},
			)
			.subscribe((status) => {
				// Handle subscription errors gracefully - errors are silently handled
				if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
					// Will retry on reconnect
				}
			});

		channelRef.current = channel;

		return () => {
			if (channelRef.current) {
				supabase.removeChannel(channelRef.current);
			}
		};
	}, [userId, supabase]);

	// Function to optimistically remove a notification
	const removeNotification = useCallback((notificationId: string) => {
		setNotifications((prev) => {
			const filtered = prev.filter((n) => n.id !== notificationId);
			// Update unread count if the removed notification was unread
			const removedNotif = prev.find((n) => n.id === notificationId);
			if (removedNotif && !removedNotif.read) {
				setUnreadCount((count) => Math.max(0, count - 1));
			}
			return filtered;
		});
	}, []);

	return {
		notifications,
		unreadCount,
		removeNotification,
		// No refetch needed - real-time handles everything
	};
}
